import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {Users, Briefcase, GraduationCap, CheckCircle2, Lock, Mail, User as UserIcon, Phone, MapPin, ShieldCheck, KeyRound, ArrowLeft, Send} from 'lucide-react';
import toast from "react-hot-toast";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import { lumi } from "@/lib/lumi";
import { saveProfile, getProfileByEmail, AccountRole, useAuth } from "@/hooks/useAuth";
import { emailCandidate_AccountCreated } from "@/lib/emails";
import { fetchCompanyProfileByCreator } from "@/hooks/useCompanyProfile";
import { trackSignUp, trackLogin } from "@/lib/analytics";
import {
  PENDING_FORMATION_DRAFT_KEY,
} from "@/hooks/useFormationDepositFlow";
import type { FormationDepositPayload } from "@/components/FormationFormModal";

/**
 * Petit utilitaire : attend que `lumi.auth.user` soit propagé après une
 * authentification réussie. Le SDK propage parfois l'utilisateur de
 * manière asynchrone (via onAuthChange), donc lire `lumi.auth.user`
 * immédiatement après `signInWithPassword` peut renvoyer `null/undefined`.
 */
async function waitForAuthUser(maxAttempts = 20, delayMs = 50): Promise<{ userId?: string; email?: string }> {
  for (let i = 0; i < maxAttempts; i++) {
    const u = lumi.auth.user;
    if (u?.userId) {
      return { userId: u.userId, email: u.email };
    }
    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }
  // Dernier essai : retourner ce qu'on a même si incomplet
  return {
    userId: lumi.auth.user?.userId,
    email: lumi.auth.user?.email,
  };
}

/**
 * Détecte si un userId correspond à un candidat en cherchant des
 * traces d'activité candidat dans Lumi (applications ou pro_dossiers).
 * Permet de retrouver le rôle "candidate" sur un nouvel appareil sans
 * localStorage.
 */
async function detectCandidateActivity(userId: string): Promise<boolean> {
  if (!userId) return false;
  try {
    // 1. A-t-il déjà postulé à une offre ?
    const apps = await lumi.entities.applications.list({
      filter: { creator: userId },
      limit: 1,
    });
    if (apps?.list && apps.list.length > 0) return true;
  } catch (err) {
    console.warn("[auth] detectCandidateActivity: applications lookup failed", err);
  }
  try {
    // 2. A-t-il un dossier pro auto-entrepreneur ?
    const dossiers = await lumi.entities.pro_dossiers.list({
      filter: { creator: userId },
      limit: 1,
    });
    if (dossiers?.list && dossiers.list.length > 0) return true;
  } catch (err) {
    console.warn("[auth] detectCandidateActivity: pro_dossiers lookup failed", err);
  }
  return false;
}

/**
 * Détecte si un userId correspond à un organisme de formation en cherchant
 * des traces d'activité organisme dans Lumi (formations publiées).
 * Permet de retrouver le rôle "organisme" sur un nouvel appareil sans
 * localStorage, lorsque le `company_profile` n'a pas encore été rempli.
 */
async function detectOrganismeActivity(userId: string): Promise<boolean> {
  if (!userId) return false;
  try {
    const formations = await lumi.entities.organisme_formations.list({
      filter: { creator: userId },
      limit: 1,
    });
    if (formations?.list && formations.list.length > 0) return true;
  } catch (err) {
    console.warn("[auth] detectOrganismeActivity: organisme_formations lookup failed", err);
  }
  return false;
}

/**
 * Détecte si un userId correspond à un recruteur en cherchant des traces
 * d'activité recruteur dans Lumi (offres publiées dans recruiter_jobs).
 * Filet de sécurité supplémentaire si le `company_profile` n'a pas encore
 * été rempli.
 */
async function detectRecruiterActivity(userId: string): Promise<boolean> {
  if (!userId) return false;
  try {
    const jobs = await lumi.entities.recruiter_jobs.list({
      filter: { creator: userId },
      limit: 1,
    });
    if (jobs?.list && jobs.list.length > 0) return true;
  } catch (err) {
    console.warn("[auth] detectRecruiterActivity: recruiter_jobs lookup failed", err);
  }
  return false;
}

type Mode = "signin" | "signup" | "reset";
type SignupStep = "form" | "verify";

// Helper : redirection post-auth selon le rôle
function redirectFor(role: AccountRole): string {
  if (role === "recruiter") return "/backoffice";
  if (role === "organisme") return "/dashboard-organisme";
  return "/dashboard";
}

/**
 * Résout le rôle métier RÉEL d'un utilisateur connecté.
 *
 * Règle métier : un email = un seul statut, et ce statut prime toujours
 * sur l'onglet sélectionné dans le formulaire. La résolution interroge,
 * dans l'ordre :
 *   1. La base Lumi `company_profiles.accountType` — vérité partagée
 *      multi-appareils pour les comptes recruiter/organisme dès qu'ils
 *      ont rempli leurs coordonnées au moins une fois.
 *   2. Heuristique organisme : présence d'au moins une formation
 *      `organisme_formations` créée par cet utilisateur (testée AVANT
 *      l'heuristique candidat pour ne pas reclasser par erreur un
 *      organisme qui aurait testé le flow candidat).
 *   3. Heuristique recruteur : présence d'au moins une offre
 *      `recruiter_jobs` créée par cet utilisateur.
 *   4. Heuristique candidat : présence d'`applications` ou de
 *      `pro_dossiers` créés par cet utilisateur.
 *   5. Le localStorage local de cet appareil (peut être vide si nouveau
 *      device ou navigation privée).
 *   6. En dernier recours, le rôle de l'onglet courant.
 */
async function resolveRealRole(params: {
  userId?: string;
  email?: string;
  fallbackRole: AccountRole;
}): Promise<AccountRole> {
  const { userId, email, fallbackRole } = params;
  console.log("[auth] resolveRealRole START", { userId, email, fallbackRole });

  // 1) Source de vérité prioritaire : `company_profiles` côté Lumi
  //    (recruteur / organisme). C'est la vérité multi-appareils dès lors
  //    que l'utilisateur a au moins ouvert son dashboard pro.
  if (userId) {
    try {
      const company = await fetchCompanyProfileByCreator(userId);
      if (company?.accountType === "recruiter" || company?.accountType === "organisme") {
        console.log("[auth] resolveRealRole → company_profiles.accountType =", company.accountType);
        return company.accountType;
      }
    } catch (err) {
      console.warn("[auth] resolveRealRole: lookup company_profiles failed", err);
    }
  }

  // 2) Heuristique organisme : présence d'au moins une formation publiée
  //    par cet utilisateur dans `organisme_formations`. Testée AVANT
  //    l'heuristique candidat pour éviter qu'un organisme ayant testé
  //    une candidature ne soit reclassé candidat.
  if (userId) {
    try {
      const isOrganismeOnServer = await detectOrganismeActivity(userId);
      if (isOrganismeOnServer) {
        console.log("[auth] resolveRealRole → activité organisme détectée côté Lumi");
        return "organisme";
      }
    } catch (err) {
      console.warn("[auth] resolveRealRole: detectOrganismeActivity failed", err);
    }
  }

  // 3) Heuristique recruteur : présence d'au moins une offre publiée
  //    par cet utilisateur dans `recruiter_jobs`.
  if (userId) {
    try {
      const isRecruiterOnServer = await detectRecruiterActivity(userId);
      if (isRecruiterOnServer) {
        console.log("[auth] resolveRealRole → activité recruiter détectée côté Lumi");
        return "recruiter";
      }
    } catch (err) {
      console.warn("[auth] resolveRealRole: detectRecruiterActivity failed", err);
    }
  }

  // 4) Heuristique candidat : présence d'activité candidat dans Lumi
  //    (applications, pro_dossiers).
  if (userId) {
    try {
      const isCandidateOnServer = await detectCandidateActivity(userId);
      if (isCandidateOnServer) {
        console.log("[auth] resolveRealRole → activité candidate détectée côté Lumi");
        return "candidate";
      }
    } catch (err) {
      console.warn("[auth] resolveRealRole: detectCandidateActivity failed", err);
    }
  }

  // 5) Fallback : profile localStorage (peut exister sur ce device)
  const localProfile = getProfileByEmail(email);
  if (localProfile?.role) {
    console.log("[auth] resolveRealRole → localStorage profile.role =", localProfile.role);
    return localProfile.role;
  }

  // 6) Dernier recours : rôle de l'onglet courant
  console.log("[auth] resolveRealRole → fallback onglet courant =", fallbackRole);
  return fallbackRole;
}

export default function Inscription() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, profile } = useAuth();

  // Rôle initial depuis l'URL (?role=organisme par ex.)
  const initialRole: AccountRole =
    (searchParams.get("role") as AccountRole) === "recruiter" ? "recruiter"
    : (searchParams.get("role") as AccountRole) === "organisme" ? "organisme"
    : "candidate";

  // Mode initial dérivé du pathname : /connexion → signin, sinon → signup
  const initialMode: Mode = location.pathname.startsWith("/connexion") ? "signin" : "signup";

  const [role, setRole] = useState<AccountRole>(initialRole);
  const [mode, setMode] = useState<Mode>(initialMode);
  const [signupStep, setSignupStep] = useState<SignupStep>("form");
  const [loading, setLoading] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);

  // Synchroniser le mode avec l'URL : indispensable car React Router ne remonte pas
  // le composant lorsqu'on navigue entre /connexion et /inscription (même élément).
  useEffect(() => {
    const wantSignin = location.pathname.startsWith("/connexion");
    setMode((current) => {
      if (wantSignin && current !== "signin") return "signin";
      if (!wantSignin && current === "signin") return "signup";
      return current;
    });
    setSignupStep("form");
    setForm((f) => ({ ...f, otp: "", password: "", passwordConfirm: "" }));
    // Synchroniser aussi le rôle si présent dans le query string
    const r = searchParams.get("role") as AccountRole | null;
    if (r === "recruiter" || r === "organisme" || r === "candidate") {
      setRole(r);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    city: "",
    password: "",
    passwordConfirm: "",
    otp: "",
    company: "",
    diploma: "BNSSA",
    accepted: false,
  });

  // Pré-remplissage depuis le brouillon de dépôt de formation (étape 1).
  // Si l'utilisateur arrive sur l'inscription organisme après avoir rempli
  // la pop-up « Annoncer une formation », on reprend ses coordonnées (nom
  // complet, nom du centre, téléphone, email) pour lui éviter une double
  // saisie. Gardé pour ne s'exécuter qu'une seule fois.
  const formationDraftApplied = useRef(false);
  useEffect(() => {
    if (formationDraftApplied.current) return;
    if (role !== "organisme") return;
    let draft: FormationDepositPayload | null = null;
    try {
      const raw = sessionStorage.getItem(PENDING_FORMATION_DRAFT_KEY);
      if (raw) draft = JSON.parse(raw) as FormationDepositPayload;
    } catch {
      draft = null;
    }
    if (!draft?.company) return;
    formationDraftApplied.current = true;
    const c = draft.company;
    // Ville (Lieu) de la formation : sert à pré-remplir le champ « Ville »
    // de l'inscription pour limiter encore la double saisie.
    const draftCity = draft.formation?.location || "";
    setForm((f) => ({
      ...f,
      fullName: f.fullName || c.responsableNom || "",
      company: f.company || c.raisonSociale || "",
      phone: f.phone || c.telephone || "",
      email: f.email || c.email || "",
      city: f.city || draftCity,
    }));
  }, [role]);

  // Already authenticated? Redirect to the proper space.
  useEffect(() => {
    if (isAuthenticated && profile) {
      navigate(redirectFor(profile.role), { replace: true });
    }
  }, [isAuthenticated, profile, navigate]);

  // OTP cooldown timer
  useEffect(() => {
    if (otpCountdown <= 0) return;
    const t = setTimeout(() => setOtpCountdown((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [otpCountdown]);

  const switchMode = (next: Mode) => {
    setMode(next);
    setSignupStep("form");
    setForm((f) => ({ ...f, otp: "", password: "", passwordConfirm: "" }));
    // Garder l'URL cohérente avec le mode (sauf reset, qui reste sur la même page)
    if (next === "signin" && !location.pathname.startsWith("/connexion")) {
      navigate("/connexion", { replace: true });
    } else if (next === "signup" && location.pathname.startsWith("/connexion")) {
      navigate("/inscription", { replace: true });
    }
  };

  const sendOtp = async (type: "register" | "resetPassword") => {
    await lumi.auth.sendOtp({ email: form.email, type });
    setOtpCountdown(60);
  };

  const handleResendOtp = async () => {
    if (!form.email || otpCountdown > 0) return;
    try {
      await sendOtp(mode === "reset" ? "resetPassword" : "register");
      toast.success("Nouveau code envoyé.");
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Impossible d'envoyer le code.";
      toast.error(message);
    }
  };

  // Step 1 of signup: validate form fields, then send OTP and switch to verify step.
  const handleSignupStart = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    if (!form.email || !form.password) {
      toast.error("Email et mot de passe requis.");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Mot de passe : 6 caractères minimum.");
      return;
    }
    if (form.password !== form.passwordConfirm) {
      toast.error("Les deux mots de passe ne correspondent pas.");
      return;
    }
    if (!form.accepted) {
      toast.error("Vous devez accepter les CGU.");
      return;
    }
    if (!form.fullName.trim()) {
      toast.error("Veuillez renseigner votre nom.");
      return;
    }
    if ((role === "recruiter" || role === "organisme") && !form.company.trim()) {
      toast.error(
        role === "organisme"
          ? "Le nom de votre centre / organisme est requis."
          : "Le nom de votre entreprise est requis."
      );
      return;
    }

    // Une adresse email = un seul statut. On bloque toute tentative
    // d'inscription dans un rôle différent de celui déjà enregistré.
    const existingProfile = getProfileByEmail(form.email);
    if (existingProfile && existingProfile.role !== role) {
      const labelFor = (r: AccountRole) =>
        r === "candidate" ? "Candidat" : r === "recruiter" ? "Recruteur" : "Organisme";
      toast.error(
        `Cet email est déjà associé à un compte ${labelFor(existingProfile.role)}. Connectez-vous avec ce statut.`
      );
      switchMode("signin");
      setRole(existingProfile.role);
      return;
    }

    setLoading(true);
    try {
      await sendOtp("register");
      setSignupStep("verify");
      toast.success("Code envoyé à " + form.email);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Impossible d'envoyer le code.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Step 2 of signup: verify OTP and create the account.
  const handleSignupVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    if (!form.otp.trim()) {
      toast.error("Veuillez saisir le code reçu par email.");
      return;
    }

    // Sécurité : si entre l'étape 1 et l'étape 2 un profile a été créé
    // dans un autre rôle, on bloque toujours.
    const existingProfile = getProfileByEmail(form.email);
    if (existingProfile && existingProfile.role !== role) {
      const labelFor = (r: AccountRole) =>
        r === "candidate" ? "Candidat" : r === "recruiter" ? "Recruteur" : "Organisme";
      toast.error(
        `Cet email est déjà associé à un compte ${labelFor(existingProfile.role)}. Connectez-vous avec ce statut.`
      );
      switchMode("signin");
      setRole(existingProfile.role);
      return;
    }

    setLoading(true);
    try {
      const cleanedOtp = form.otp.replace(/\D/g, "");
      if (cleanedOtp.length < 4) {
        toast.error("Le code de vérification doit contenir 6 chiffres.");
        setLoading(false);
        return;
      }
      await lumi.auth.signUpWithPassword({
        email: form.email,
        password: form.password,
        otp: cleanedOtp,
        userName: form.fullName,
      });
      saveProfile({
        email: form.email.toLowerCase(),
        role,
        fullName: form.fullName,
        company: role === "recruiter" ? form.company : undefined,
        phone: form.phone || undefined,
        city: form.city || undefined,
        diploma: role === "candidate" ? form.diploma : undefined,
        createdAt: new Date().toISOString(),
      });

      // Pour les comptes pro (recruiter / organisme), on crée immédiatement
      // un `company_profile` minimal portant `accountType`. Cela rend le
      // rôle détectable côté serveur dès la première connexion sur un autre
      // appareil (ou en navigation privée), avant même que l'utilisateur
      // n'ait rempli ses coordonnées complètes ou publié une offre/formation.
      if (role === "recruiter" || role === "organisme") {
        try {
          const { userId: signedUpUserId, email: signedUpEmail } = await waitForAuthUser();
          if (signedUpUserId) {
            const existingCompany = await fetchCompanyProfileByCreator(signedUpUserId);
            if (!existingCompany) {
              const nowIso = new Date().toISOString();
              await lumi.entities.company_profiles.create({
                creator: signedUpUserId,
                creatorEmail: (signedUpEmail || form.email).toLowerCase(),
                accountType: role,
                raisonSociale: form.company || "",
                siret: "",
                adresse: "",
                telephone: form.phone || "",
                responsableNom: form.fullName || "",
                logoUrl: "",
                createdAt: nowIso,
                updatedAt: nowIso,
              });
            }
          }
        } catch (err) {
          // Ne bloque pas la création du compte si l'init du profile pro échoue ;
          // l'utilisateur pourra toujours remplir ses coordonnées plus tard.
          console.warn("[auth] init company_profile (signup) failed", err);
        }
      }
      // Email 7 — Bienvenue (best-effort, non bloquant)
      void emailCandidate_AccountCreated({
        candidateEmail: form.email,
        candidateName: form.fullName,
        role,
      });
      // GA4 — Inscription réussie (sans PII)
      trackSignUp({ role, method: "password" });
      // Nettoyage d'un éventuel brouillon de formation obsolète : s'il reste
      // alors que l'utilisateur s'inscrit en candidat/recruteur, il ne sera
      // jamais consommé (le dashboard organisme ne s'ouvre pas pour eux).
      if (role !== "organisme") {
        try { sessionStorage.removeItem(PENDING_FORMATION_DRAFT_KEY); } catch { /* noop */ }
      }
      toast.success("Compte créé avec succès !");
      navigate(redirectFor(role), { replace: true });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Code invalide ou expiré.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Sign-in handler (no OTP).
  // Règle métier : un email = un seul statut. À la connexion, on IGNORE
  // l'onglet sélectionné et on utilise uniquement le rôle déjà enregistré
  // pour cet utilisateur. La source de vérité est `company_profiles` côté
  // Lumi (partagé multi-devices), avec fallback sur le localStorage puis
  // sur le rôle de l'onglet si rien n'existe encore.
  const handleSignin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    if (!form.email || !form.password) {
      toast.error("Email et mot de passe requis.");
      return;
    }
    setLoading(true);
    try {
      await lumi.auth.signInWithPassword({ email: form.email, password: form.password });

      // Important : attendre que le SDK Lumi ait propagé `lumi.auth.user`
      // (asynchrone via onAuthChange) avant de lire userId — sinon la
      // résolution du rôle réel est court-circuitée et on retombe sur
      // l'onglet courant, ce qui causait les redirections incorrectes.
      const { userId: resolvedUserId, email: resolvedEmail } = await waitForAuthUser();
      const userId = resolvedUserId;
      const userEmail = resolvedEmail || form.email;

      const realRole = await resolveRealRole({
        userId,
        email: userEmail,
        fallbackRole: role,
      });

      // Persiste / met à jour le profile local pour que toutes les pages
      // (Header, Dashboard, etc.) voient immédiatement le bon statut.
      const existing = getProfileByEmail(userEmail);
      saveProfile({
        ...(existing || {}),
        email: userEmail.toLowerCase(),
        role: realRole,
        createdAt: existing?.createdAt || new Date().toISOString(),
      });

      if (realRole !== role) {
        const labelFor = (r: AccountRole) =>
          r === "candidate" ? "Candidat" : r === "recruiter" ? "Recruteur" : "Organisme";
        toast(
          `Connexion en tant que ${labelFor(realRole)} — c'est le statut associé à votre email.`,
          { icon: "ℹ️", duration: 5000 }
        );
      } else {
        toast.success("Connexion réussie !");
      }
      // GA4 — Connexion réussie (email/mot de passe), sans PII
      trackLogin({ role: realRole, method: "password" });
      navigate(redirectFor(realRole), { replace: true });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Identifiants incorrects.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Reset password handler (with OTP).
  // Même règle : on respecte le rôle réel attaché à l'utilisateur,
  // résolu via la base Lumi en priorité.
  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    if (!form.email || !form.password || !form.otp) {
      toast.error("Email, code et nouveau mot de passe requis.");
      return;
    }
    setLoading(true);
    try {
      await lumi.auth.resetPassword({
        email: form.email,
        newPassword: form.password,
        otp: form.otp.replace(/\D/g, ""),
      });

      // Idem signin : attendre la propagation de lumi.auth.user.
      const { userId: resolvedUserId, email: resolvedEmail } = await waitForAuthUser();
      const userId = resolvedUserId;
      const userEmail = resolvedEmail || form.email;

      const realRole = await resolveRealRole({
        userId,
        email: userEmail,
        fallbackRole: role,
      });

      const existing = getProfileByEmail(userEmail);
      saveProfile({
        ...(existing || {}),
        email: userEmail.toLowerCase(),
        role: realRole,
        createdAt: existing?.createdAt || new Date().toISOString(),
      });

      toast.success("Mot de passe réinitialisé !");
      navigate(redirectFor(realRole), { replace: true });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Code invalide ou expiré.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Google OAuth : on respecte aussi le rôle réel de l'utilisateur,
  // résolu via la base Lumi (`company_profiles`) en priorité. L'onglet
  // ne sert que de fallback si rien n'existe encore.
  const handleGoogle = async () => {
    try {
      await lumi.auth.signInWithOAuth({ provider: "google" });

      // Idem : attendre la propagation effective de l'utilisateur.
      const { userId, email: userEmail } = await waitForAuthUser();
      if (!userEmail) return;

      const realRole = await resolveRealRole({
        userId,
        email: userEmail,
        fallbackRole: role,
      });

      const existing = getProfileByEmail(userEmail);
      saveProfile({
        ...(existing || {}),
        email: userEmail.toLowerCase(),
        role: realRole,
        createdAt: existing?.createdAt || new Date().toISOString(),
      });

      if (realRole !== role) {
        const labelFor = (r: AccountRole) =>
          r === "candidate" ? "Candidat" : r === "recruiter" ? "Recruteur" : "Organisme";
        toast(
          `Connexion en tant que ${labelFor(realRole)} (statut associé à votre email).`,
          { icon: "ℹ️" }
        );
      }

      // GA4 — Connexion réussie via Google OAuth, sans PII
      trackLogin({ role: realRole, method: "oauth_google" });
      navigate(redirectFor(realRole), { replace: true });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Connexion Google impossible.";
      toast.error(message);
    }
  };

  const accent = role === "candidate" ? "blue" : role === "organisme" ? "amber" : "gray";

  // Active form handler
  const onSubmit =
    mode === "signin"
      ? handleSignin
      : mode === "reset"
      ? handleReset
      : signupStep === "form"
      ? handleSignupStart
      : handleSignupVerify;

  return (
    <Layout>
      <Seo
        title={
          mode === "signup"
            ? "Inscription gratuite — Candidat, Recruteur ou Organisme | Emplois BNSSA & MNS"
            : mode === "reset"
            ? "Réinitialiser mon mot de passe | Emplois BNSSA & MNS"
            : "Connexion à votre espace | Emplois BNSSA & MNS"
        }
        description="Créez votre compte gratuit sur Emplois BNSSA & MNS pour postuler aux offres ou recruter des candidats qualifiés."
        canonical={
          mode === "signin" ? "https://bnssa-mns.fr/connexion" : "https://bnssa-mns.fr/inscription"
        }
      />

      <section className="min-h-[calc(100vh-200px)] bg-gradient-to-br from-slate-50 via-white to-slate-100 py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden">
            {/* Left visual */}
            <div
              className={`hidden lg:block relative p-10 text-white ${
                role === "candidate"
                  ? "bg-gradient-to-br from-blue-600 to-indigo-700"
                  : role === "organisme"
                  ? "bg-gradient-to-br from-amber-600 via-orange-600 to-red-600"
                  : "bg-gradient-to-br from-gray-900 to-slate-800"
              }`}
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" aria-hidden></div>
              <div className="relative">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mb-6">
                  {role === "candidate" ? <Users className="w-6 h-6" />
                    : role === "organisme" ? <GraduationCap className="w-6 h-6" />
                    : <Briefcase className="w-6 h-6" />}
                </div>
                <h2 className="text-3xl font-bold mb-4">
                  {role === "candidate" ? "Espace Candidat"
                    : role === "organisme" ? "Espace Organisme"
                    : "Espace Recruteur"}
                </h2>
                <p className="text-white/90 mb-8 leading-relaxed">
                  {role === "candidate"
                    ? "Créez votre profil, postulez en un clic et recevez des offres ciblées BNSSA et MNS."
                    : role === "organisme"
                    ? "Annoncez gratuitement vos sessions de formation BNSSA, BPJEPS AAN, BEESAN, recyclages… et touchez les candidats qualifiés."
                    : "Publiez vos offres, gérez vos candidatures et trouvez les meilleurs profils qualifiés."}
                </p>
                <ul className="space-y-3 text-sm">
                  {(role === "candidate"
                    ? [
                        "Inscription 100% gratuite",
                        "Candidature en un clic",
                        "Alertes par email personnalisées",
                        "Profil consultable par les recruteurs",
                      ]
                    : role === "organisme"
                    ? [
                        "Publication gratuite illimitée",
                        "Visibilité nationale par région",
                        "Tableau de bord dédié",
                        "Inscriptions directes par vos coordonnées",
                      ]
                    : [
                        "Première offre offerte",
                        "Candidats vérifiés et qualifiés",
                        "Tableau de bord intuitif",
                        "Mise en avant possible",
                      ]).map((item) => (
                    <li key={item} className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 flex-shrink-0" aria-hidden />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Form */}
            <div className="p-8 sm:p-10">
              {/* Role tabs (hidden in reset mode and verify step) */}
              {mode !== "reset" && !(mode === "signup" && signupStep === "verify") && (
                <div className="grid grid-cols-3 gap-1 p-1 bg-gray-100 rounded-lg mb-6" role="tablist" aria-label="Type de compte">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={role === "candidate"}
                    onClick={() => setRole("candidate")}
                    className={`py-2 rounded-md font-semibold text-xs sm:text-sm transition-colors ${
                      role === "candidate" ? "bg-blue-600 text-white shadow-sm" : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    Candidat
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={role === "recruiter"}
                    onClick={() => setRole("recruiter")}
                    className={`py-2 rounded-md font-semibold text-xs sm:text-sm transition-colors ${
                      role === "recruiter" ? "bg-gray-900 text-white shadow-sm" : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    Recruteur
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={role === "organisme"}
                    onClick={() => setRole("organisme")}
                    className={`py-2 rounded-md font-semibold text-xs sm:text-sm transition-colors ${
                      role === "organisme" ? "bg-amber-600 text-white shadow-sm" : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    Organisme
                  </button>
                </div>
              )}

              <h1 className="text-2xl font-bold text-gray-900 mb-1">
                {mode === "signup" && signupStep === "verify"
                  ? "Vérifiez votre email"
                  : mode === "signup"
                  ? "Créer un compte"
                  : mode === "reset"
                  ? "Réinitialiser mon mot de passe"
                  : "Se connecter"}
              </h1>
              <p className="text-sm text-gray-600 mb-6">
                {mode === "signup" && signupStep === "verify" ? (
                  <>
                    Un code de 6 chiffres a été envoyé à{" "}
                    <span className="font-semibold text-gray-900">{form.email}</span>.
                  </>
                ) : mode === "signup" ? (
                  "Inscription gratuite et sécurisée en moins d'une minute."
                ) : mode === "reset" ? (
                  "Saisissez votre email, recevez un code et choisissez un nouveau mot de passe."
                ) : (
                  "Heureux de vous revoir !"
                )}
              </p>

              <form onSubmit={onSubmit} className="space-y-4" noValidate>
                {/* ───────── SIGNUP STEP 1: profile fields ───────── */}
                {mode === "signup" && signupStep === "form" && (
                  <>
                    <div>
                      <label htmlFor="fullName" className="block text-xs font-semibold text-gray-700 mb-1">
                        {role === "recruiter" ? "Nom du contact *" : "Nom complet *"}
                      </label>
                      <div className="relative">
                        <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden />
                        <input
                          id="fullName"
                          type="text"
                          required
                          value={form.fullName}
                          onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                          className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                      </div>
                    </div>

                    {role === "recruiter" && (
                      <div>
                        <label htmlFor="company" className="block text-xs font-semibold text-gray-700 mb-1">
                          Entreprise / Établissement *
                        </label>
                        <input
                          id="company"
                          type="text"
                          required
                          value={form.company}
                          onChange={(e) => setForm({ ...form, company: e.target.value })}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                      </div>
                    )}

                    {role === "organisme" && (
                      <div>
                        <label htmlFor="company" className="block text-xs font-semibold text-gray-700 mb-1">
                          Nom du centre / organisme de formation *
                        </label>
                        <input
                          id="company"
                          type="text"
                          required
                          value={form.company}
                          onChange={(e) => setForm({ ...form, company: e.target.value })}
                          placeholder="Ex : CREPS Île-de-France, Aqua Sécurité Formation…"
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 outline-none"
                        />
                      </div>
                    )}

                    {role === "candidate" && (
                      <div>
                        <label htmlFor="diploma" className="block text-xs font-semibold text-gray-700 mb-1">
                          Diplôme principal *
                        </label>
                        <select
                          id="diploma"
                          value={form.diploma}
                          onChange={(e) => setForm({ ...form, diploma: e.target.value })}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                        >
                          <option value="BNSSA">BNSSA</option>
                          <option value="BPJEPS AAN">BPJEPS AAN</option>
                          <option value="BEESAN">BEESAN</option>
                          <option value="MNS">MNS</option>
                          <option value="En formation">En formation</option>
                        </select>
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label htmlFor="phone" className="block text-xs font-semibold text-gray-700 mb-1">
                          Téléphone
                        </label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden />
                          <input
                            id="phone"
                            type="tel"
                            value={form.phone}
                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="city" className="block text-xs font-semibold text-gray-700 mb-1">
                          Ville
                        </label>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden />
                          <input
                            id="city"
                            type="text"
                            value={form.city}
                            onChange={(e) => setForm({ ...form, city: e.target.value })}
                            className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* ───────── EMAIL field (signup step 1, signin, reset) ───────── */}
                {!(mode === "signup" && signupStep === "verify") && (
                  <div>
                    <label htmlFor="email" className="block text-xs font-semibold text-gray-700 mb-1">
                      Email *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden />
                      <input
                        id="email"
                        type="email"
                        required
                        autoComplete="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                    </div>
                  </div>
                )}

                {/* ───────── PASSWORD field (signup step 1, signin, reset) ───────── */}
                {!(mode === "signup" && signupStep === "verify") && (
                  <div>
                    <label htmlFor="password" className="block text-xs font-semibold text-gray-700 mb-1">
                      {mode === "reset" ? "Nouveau mot de passe *" : "Mot de passe *"}
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden />
                      <input
                        id="password"
                        type="password"
                        required
                        minLength={6}
                        autoComplete={mode === "signin" ? "current-password" : "new-password"}
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                    </div>
                  </div>
                )}

                {/* ───────── PASSWORD CONFIRM (signup step 1 only) ───────── */}
                {mode === "signup" && signupStep === "form" && (
                  <div>
                    <label htmlFor="passwordConfirm" className="block text-xs font-semibold text-gray-700 mb-1">
                      Confirmer le mot de passe *
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden />
                      <input
                        id="passwordConfirm"
                        type="password"
                        required
                        minLength={6}
                        autoComplete="new-password"
                        value={form.passwordConfirm}
                        onChange={(e) => setForm({ ...form, passwordConfirm: e.target.value })}
                        className={`w-full pl-10 pr-3 py-2.5 border rounded-lg text-sm focus:ring-2 outline-none ${
                          form.passwordConfirm && form.password !== form.passwordConfirm
                            ? "border-red-300 focus:ring-red-500"
                            : form.passwordConfirm && form.password === form.passwordConfirm
                            ? "border-green-300 focus:ring-green-500"
                            : "border-gray-300 focus:ring-blue-500"
                        }`}
                      />
                    </div>
                    {form.passwordConfirm && form.password !== form.passwordConfirm && (
                      <p className="text-[11px] text-red-600 mt-1">
                        Les deux mots de passe ne correspondent pas.
                      </p>
                    )}
                    {form.passwordConfirm && form.password === form.passwordConfirm && (
                      <p className="text-[11px] text-green-600 mt-1 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        Les mots de passe correspondent.
                      </p>
                    )}
                  </div>
                )}

                {/* ───────── CGU checkbox (signup step 1) ───────── */}
                {mode === "signup" && signupStep === "form" && (
                  <label className="flex items-start gap-2 text-xs text-gray-600">
                    <input
                      type="checkbox"
                      checked={form.accepted}
                      onChange={(e) => setForm({ ...form, accepted: e.target.checked })}
                      className="mt-0.5"
                    />
                    <span>
                      J'accepte les{" "}
                      <Link to="/cgu" className="text-blue-600 underline">CGU</Link> et la{" "}
                      <Link to="/politique-confidentialite" className="text-blue-600 underline">
                        politique de confidentialité
                      </Link>.
                    </span>
                  </label>
                )}

                {/* ───────── OTP field (reset mode) ───────── */}
                {mode === "reset" && (
                  <div>
                    <label htmlFor="otp" className="block text-xs font-semibold text-gray-700 mb-1">
                      Code de vérification *
                    </label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden />
                        <input
                          id="otp"
                          type="text"
                          inputMode="numeric"
                          required
                          value={form.otp}
                          onChange={(e) => setForm({ ...form, otp: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                          placeholder="6 chiffres"
                          className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none tracking-widest"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={otpCountdown > 0 || !form.email}
                        className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        {otpCountdown > 0 ? `${otpCountdown}s` : "Envoyer"}
                      </button>
                    </div>
                  </div>
                )}

                {/* ───────── SIGNUP STEP 2: OTP verify ───────── */}
                {mode === "signup" && signupStep === "verify" && (
                  <>
                    <div>
                      <label htmlFor="otp" className="block text-xs font-semibold text-gray-700 mb-1">
                        Code de vérification (6 chiffres) *
                      </label>
                      <div className="relative">
                        <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden />
                        <input
                          id="otp"
                          type="text"
                          inputMode="numeric"
                          autoFocus
                          required
                          value={form.otp}
                          onChange={(e) => setForm({ ...form, otp: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                          placeholder="123456"
                          className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-blue-500 outline-none tracking-[0.35em] font-semibold"
                        />
                      </div>
                      <p className="text-[11px] text-gray-500 mt-1">
                        Pensez à vérifier vos spams si l'email tarde à arriver.
                      </p>
                    </div>

                    <div className="flex items-center justify-between text-xs">
                      <button
                        type="button"
                        onClick={() => setSignupStep("form")}
                        className="text-gray-600 hover:text-gray-900 inline-flex items-center gap-1"
                      >
                        <ArrowLeft className="w-3.5 h-3.5" /> Modifier mes infos
                      </button>
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={otpCountdown > 0}
                        className="text-blue-600 font-semibold hover:underline disabled:opacity-60 disabled:no-underline disabled:cursor-not-allowed inline-flex items-center gap-1"
                      >
                        <Send className="w-3.5 h-3.5" />
                        {otpCountdown > 0 ? `Renvoyer (${otpCountdown}s)` : "Renvoyer le code"}
                      </button>
                    </div>
                  </>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full py-3 rounded-lg font-semibold text-sm text-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed ${
                    accent === "blue" ? "bg-blue-600 hover:bg-blue-700"
                      : accent === "amber" ? "bg-amber-600 hover:bg-amber-700"
                      : "bg-gray-900 hover:bg-black"
                  }`}
                >
                  <ShieldCheck className="w-4 h-4" aria-hidden />
                  {loading
                    ? "Veuillez patienter…"
                    : mode === "signup" && signupStep === "form"
                    ? "Continuer"
                    : mode === "signup" && signupStep === "verify"
                    ? "Créer mon compte"
                    : mode === "reset"
                    ? "Réinitialiser le mot de passe"
                    : "Se connecter"}
                </button>

                {/* Google OAuth (signin and signup step 1 only) */}
                {mode !== "reset" && !(mode === "signup" && signupStep === "verify") && (
                  <button
                    type="button"
                    onClick={handleGoogle}
                    disabled={loading}
                    className="w-full py-2.5 border border-gray-300 rounded-lg font-semibold text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 48 48" aria-hidden>
                      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.7 4.7-6.2 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
                      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                      <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 35 26.7 36 24 36c-5.1 0-9.5-3.3-11.2-7.9l-6.6 5C9.6 39.6 16.3 44 24 44z"/>
                      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.4 4.3-4.4 5.6l6.3 5.3C40.9 36 44 30.5 44 24c0-1.3-.1-2.3-.4-3.5z"/>
                    </svg>
                    Continuer avec Google
                  </button>
                )}

                {/* Mode switcher */}
                <div className="text-center text-sm text-gray-600 space-y-2">
                  {mode === "signin" && (
                    <>
                      <p>
                        <button
                          type="button"
                          onClick={() => switchMode("reset")}
                          className="text-blue-600 font-semibold hover:underline"
                        >
                          Mot de passe oublié ?
                        </button>
                      </p>
                      <p>
                        Pas encore inscrit ?{" "}
                        <button
                          type="button"
                          onClick={() => switchMode("signup")}
                          className="text-blue-600 font-semibold hover:underline"
                        >
                          Créer un compte
                        </button>
                      </p>
                    </>
                  )}
                  {mode === "signup" && signupStep === "form" && (
                    <p>
                      Déjà inscrit ?{" "}
                      <button
                        type="button"
                        onClick={() => switchMode("signin")}
                        className="text-blue-600 font-semibold hover:underline"
                      >
                        Se connecter
                      </button>
                    </p>
                  )}
                  {mode === "reset" && (
                    <p>
                      <button
                        type="button"
                        onClick={() => switchMode("signin")}
                        className="text-blue-600 font-semibold hover:underline inline-flex items-center gap-1"
                      >
                        <ArrowLeft className="w-3.5 h-3.5" /> Retour à la connexion
                      </button>
                    </p>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
